import { type HppdPostNumbers, type LedgerEntry, type PostNumbers, RequestOriginKind, StatsMode } from "../../../utils/models";
import type { FactListItem } from "../../UIToolkit/molecules/FactList";
import { formatAt, leadFact, type OriginCopyContext, ratioRoleLabel } from "./requestOriginCopy";

/** The numbers an auto-post or a recommendation post rested on, or `null` for an entry that rests on none. */
export const numbersOf = (entry: LedgerEntry): PostNumbers | null => {
	if (entry.details.kind === RequestOriginKind.AutoPost) return entry.details.numbers;
	if (entry.details.kind === RequestOriginKind.FromRecommendation) return entry.details.numbers;
	return null;
};

export const hppdNumbersOf = (entry: LedgerEntry): HppdPostNumbers | null => {
	const numbers = numbersOf(entry);
	return numbers !== null && numbers.kind === StatsMode.PPD_HPPD ? numbers : null;
};

const numberFacts = (numbers: PostNumbers, ctx: OriginCopyContext): FactListItem[] => {
	const { t } = ctx;
	if (numbers.kind === StatsMode.PPD_HPPD) return [{ label: t("request.origin.fact.census"), value: numbers.census }];
	return [
		{ label: t("request.origin.fact.required"), value: `${numbers.required} ${ratioRoleLabel(numbers.job_id, numbers.role_group, ctx)}` },
		{ label: t("request.origin.fact.scheduled"), value: numbers.scheduled },
		{ label: t("request.origin.fact.alreadyPosted"), value: numbers.posted },
	];
};

/** The rows under a lone entry's sentence: the numbers it rests on (if any), the agency (if any), then when and how far ahead. */
export const entryFacts = (entry: LedgerEntry, ctx: OriginCopyContext): FactListItem[] => {
	const { t } = ctx;
	const timing: FactListItem[] = [];
	if (entry.at !== null) timing.push({ label: t("request.origin.fact.posted"), value: formatAt(entry.at, ctx) });
	if (entry.lead_minutes !== null) timing.push({ label: t("request.origin.fact.leadTime"), value: leadFact(entry.lead_minutes, ctx) });
	const numbers = numbersOf(entry);
	if (numbers !== null) return [...numberFacts(numbers, ctx), ...timing];
	if (entry.details.kind === RequestOriginKind.Agency || entry.details.kind === RequestOriginKind.AgencyCanceled) {
		return [{ label: t("request.origin.fact.agency"), value: entry.details.agency_name }, ...timing];
	}
	return timing;
};
